import React, { useState, useCallback, useRef, useEffect } from 'react';
import StarRating from './StarRating';
import VoiceRecorder from './VoiceRecorder';
import { submitReview, uploadVoiceReview, fetchCollegeAutocomplete, fetchCourseOptions } from './realTalkApi';

const INITIAL_RATINGS = { academics: 0, placements: 0, campus_life: 0, faculty: 0, value_for_money: 0 };
const RATING_LABELS = { academics: 'Academics', placements: 'Placements', campus_life: 'Campus Life', faculty: 'Faculty', value_for_money: 'Value for Money' };

const FALLBACK_COURSES = ['B.Tech', 'B.E.', 'BBA', 'BCA', 'B.Com', 'B.Sc', 'BA', 'B.Arch', 'MBBS', 'BDS', 'B.Pharm', 'LLB', 'B.Des', 'BHM', 'B.Ed', 'M.Tech', 'MBA', 'MCA', 'M.Com', 'M.Sc', 'MA', 'M.Arch', 'MD', 'MS', 'M.Pharm', 'LLM', 'M.Des', 'PhD', 'Other'];
const FALLBACK_YEARS = ['1st Year', '2nd Year', '3rd Year', '4th Year', 'Alumni'];

export default function SubmissionForm({ onSuccess }) {
  const [collegeName, setCollegeName] = useState('');
  const [course, setCourse] = useState('');
  const [year, setYear] = useState('');
  const [city, setCity] = useState('');
  const [reviewType, setReviewType] = useState('written');
  const [reviewTitle, setReviewTitle] = useState('');
  const [ratings, setRatings] = useState(INITIAL_RATINGS);
  const [writtenReview, setWrittenReview] = useState('');
  const [wishIKnew, setWishIKnew] = useState('');
  const [wouldChooseAgain, setWouldChooseAgain] = useState('');
  const [displayMode, setDisplayMode] = useState('anonymous'); // anonymous | first_name | full
  const [consent, setConsent] = useState(false);
  const [reviewerName, setReviewerName] = useState('');
  const [mobile, setMobile] = useState('');
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [courseOptions, setCourseOptions] = useState(FALLBACK_COURSES);
  const [yearOptions, setYearOptions] = useState(FALLBACK_YEARS);
  const [showVerify, setShowVerify] = useState(false);
  const startedRef = useRef(false);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchCourseOptions()
      .then((data) => { setCourseOptions(data.courses || FALLBACK_COURSES); setYearOptions(data.years || FALLBACK_YEARS); })
      .catch(() => {});
  }, []);

  const handleCollegeInput = useCallback((val) => {
    setCollegeName(val);
    if (!startedRef.current && val.length > 0) {
      startedRef.current = true;
      if (typeof window !== 'undefined' && window.gtag) window.gtag('event', 'review_submission_started', { page_path: '/campus-truth' });
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      const results = await fetchCollegeAutocomplete(val);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    }, 300);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleRatingChange = (category, value) => { setRatings((prev) => ({ ...prev, [category]: value })); };

  const validate = () => {
    if (!collegeName.trim()) return 'Please enter your college name';
    if (!course) return 'Please select your course';
    if (!year) return 'Please select your year';
    const unrated = Object.entries(ratings).find(([, v]) => v === 0);
    if (unrated) return `Please rate ${RATING_LABELS[unrated[0]]}`;
    if (writtenReview.trim().length < 50) return 'Review must be at least 50 characters';
    if (writtenReview.trim().length > 500) return 'Review must be under 500 characters';
    if (mobile && !/^\d{10}$/.test(mobile.replace(/\D/g, ''))) return 'Please enter a valid 10-digit mobile number';
    if (reviewType === 'voice' && !voiceBlob) return 'Please record or upload a voice review';
    if (!consent) return 'Please confirm the consent statement to submit';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSubmitting(true);
    try {
      const payload = {
        college_name: collegeName.trim(), course, year, ratings,
        written_review: writtenReview.trim(),
        reviewer_name: displayMode === 'anonymous' ? null : (reviewerName.trim() || null),
        mobile: mobile.replace(/\D/g, '') || null,
        is_anonymous: displayMode === 'anonymous',
        review_type: reviewType,
        review_title: reviewTitle.trim() || null,
        city: city.trim() || null,
        state: null,
        wish_i_knew: wishIKnew.trim() || null,
        would_choose_again: wouldChooseAgain || null,
        display_mode: displayMode,
        consent,
      };
      const result = await submitReview(payload);
      if (voiceBlob && result.review_id) {
        try {
          await uploadVoiceReview(result.review_id, voiceBlob);
          if (typeof window !== 'undefined' && window.gtag) window.gtag('event', 'voice_review_uploaded', { review_id: result.review_id });
        } catch { /* voice upload optional */ }
      }
      setSubmitted(true);
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'review_submission_completed', {
          college_name: collegeName.trim(), course, year, review_type: reviewType,
          overall_rating: (Object.values(ratings).reduce((a, b) => a + b, 0) / 5).toFixed(1),
          has_voice: !!voiceBlob,
        });
      }
      if (onSuccess) onSuccess(result);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="rt-success-state" id="rt-submission-form" data-testid="rt-success-state">
        <div className="rt-confetti-container" aria-hidden="true">
          {Array.from({ length: 30 }).map((_, i) => (
            <span key={i} className="rt-confetti-piece" style={{ '--x': `${Math.random() * 100}%`, '--delay': `${Math.random() * 0.5}s`, '--color': ['#D4A24C', '#0B2352', '#22c55e', '#ef4444', '#3b82f6'][i % 5] }} />
          ))}
        </div>
        <div className="rt-success-content">
          <div className="rt-success-icon">🔥</div>
          <h3 className="rt-success-title">Your verdict is in!</h3>
          <p className="rt-success-text">You're entered for this month's mystery gift 🎁</p>
          <p className="rt-success-note">Your review will appear after moderation.</p>
          <button onClick={() => { setSubmitted(false); setCollegeName(''); setCourse(''); setYear(''); setCity(''); setReviewType('written'); setReviewTitle(''); setRatings(INITIAL_RATINGS); setWrittenReview(''); setWishIKnew(''); setWouldChooseAgain(''); setDisplayMode('anonymous'); setConsent(false); setReviewerName(''); setMobile(''); setVoiceBlob(null); startedRef.current = false; }} className="rt-submit-another-btn" data-testid="rt-submit-another">Submit Another Verdict</button>
        </div>
      </div>
    );
  }

  return (
    <section className="rt-form-section" id="rt-submission-form">
      <div className="rt-form-container">
        <h2 className="rt-form-title">Share Your Campus Truth</h2>
        <p className="rt-form-subtitle">Be honest. Be real. Help the next batch. कॉलेज का असली सच जानें.</p>

        {/* Verification & guidelines — collapsible, before submission */}
        <div className="rt-verify-block" data-testid="ct-verify-block">
          <button type="button" className="rt-verify-toggle" onClick={() => setShowVerify((s) => !s)} data-testid="ct-verify-toggle" aria-expanded={showVerify}>
            <span>How we verify Campus Truth reviews</span>
            <span>{showVerify ? '−' : '+'}</span>
          </button>
          {showVerify && (
            <div className="rt-verify-content" data-testid="ct-verify-content">
              <ol>
                <li>We check whether the reviewer has shared sufficient college and course details.</li>
                <li>We may privately request supporting proof, such as a college ID, fee receipt or official college email.</li>
                <li>We review submissions for relevance, spam, abusive language and misleading claims.</li>
                <li>We publish honest experiences — positive, negative or mixed — when they meet our community guidelines.</li>
                <li>Personal documents are never displayed publicly.</li>
              </ol>
              <p style={{ marginTop: 10, fontWeight: 600, color: '#0B2352' }}>Community Guidelines</p>
              <ul>
                <li>Share your own experience only.</li>
                <li>Do not publish personal numbers, addresses or private information.</li>
                <li>No abuse, hate speech, discrimination, threats or defamatory allegations.</li>
                <li>Reviews may be edited only for privacy, clarity or policy compliance.</li>
                <li>CollegeUnlock does not guarantee admission outcomes or endorse every individual opinion.</li>
              </ul>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="rt-form" noValidate data-testid="rt-form">
          <div className="rt-field" ref={suggestionsRef}>
            <label className="rt-form-label">College Name *</label>
            <input type="text" value={collegeName} onChange={(e) => handleCollegeInput(e.target.value)} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} placeholder="e.g. IIT Bombay, VIT Vellore, Christ University" className="rt-input" maxLength={200} data-testid="rt-college-input" />
            {showSuggestions && (
              <ul className="rt-autocomplete-list">
                {suggestions.map((name, idx) => (<li key={idx} onClick={() => { setCollegeName(name); setShowSuggestions(false); }} className="rt-autocomplete-item">{name}</li>))}
              </ul>
            )}
          </div>
          <div className="rt-field">
            <label className="rt-form-label">City <span className="rt-optional">(optional)</span></label>
            <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Bengaluru, Pune" className="rt-input" maxLength={80} data-testid="ct-city-input" />
          </div>
          <div className="rt-field">
            <label className="rt-form-label">Course Studied *</label>
            <select value={course} onChange={(e) => setCourse(e.target.value)} className="rt-select" data-testid="rt-course-select">
              <option value="">Select your course</option>
              {courseOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div className="rt-field">
            <label className="rt-form-label">Year of Study / Graduation *</label>
            <select value={year} onChange={(e) => setYear(e.target.value)} className="rt-select" data-testid="rt-year-select">
              <option value="">Select year</option>
              {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
          <div className="rt-field">
            <label className="rt-form-label">Review Type *</label>
            <div style={{ display: 'flex', gap: 8 }} role="radiogroup">
              <button type="button" role="radio" aria-checked={reviewType === 'written'} onClick={() => setReviewType('written')} className={`rt-tab ${reviewType === 'written' ? 'rt-tab-active' : ''}`} data-testid="ct-type-written">Written Review</button>
              <button type="button" role="radio" aria-checked={reviewType === 'voice'} onClick={() => setReviewType('voice')} className={`rt-tab ${reviewType === 'voice' ? 'rt-tab-active' : ''}`} data-testid="ct-type-voice">Voice Review</button>
            </div>
          </div>
          <div className="rt-ratings-section">
            <label className="rt-form-label">Rate Your Experience *</label>
            <div className="rt-ratings-grid">
              {Object.keys(RATING_LABELS).map((key) => (<StarRating key={key} label={RATING_LABELS[key]} value={ratings[key]} onChange={(val) => handleRatingChange(key, val)} size="md" />))}
            </div>
          </div>
          <div className="rt-field">
            <label className="rt-form-label">Review Title *</label>
            <input type="text" value={reviewTitle} onChange={(e) => setReviewTitle(e.target.value)} placeholder="e.g. Great faculty but placements need work" className="rt-input" maxLength={120} data-testid="ct-title-input" />
          </div>
          {reviewType === 'voice' && (
            <>
              <VoiceRecorder onRecordingComplete={setVoiceBlob} />
              <p className="rt-field-hint" style={{ color: '#666' }}>Voice reviews are reviewed and verified before publication.</p>
            </>
          )}
          <div className="rt-field">
            <label className="rt-form-label">Your Written Verdict *<span className="rt-char-count">{writtenReview.length}/500</span></label>
            <textarea value={writtenReview} onChange={(e) => setWrittenReview(e.target.value)} placeholder="Tell it like it is. What's great? What sucks? Would you join again?" className="rt-textarea" rows={4} minLength={50} maxLength={500} data-testid="rt-review-textarea" />
            {writtenReview.length > 0 && writtenReview.length < 50 && (<span className="rt-field-hint">{50 - writtenReview.length} more characters needed</span>)}
          </div>
          <div className="rt-field">
            <label className="rt-form-label">What I wish I knew before joining <span className="rt-optional">(recommended)</span></label>
            <textarea value={wishIKnew} onChange={(e) => setWishIKnew(e.target.value)} placeholder="What advice would you give your past self?" className="rt-textarea" rows={2} maxLength={400} data-testid="ct-wish-textarea" />
          </div>
          <div className="rt-field">
            <label className="rt-form-label">Would you choose this college again?</label>
            <div style={{ display: 'flex', gap: 8 }} role="radiogroup">
              {['yes', 'no', 'maybe'].map((v) => (
                <button key={v} type="button" role="radio" aria-checked={wouldChooseAgain === v} onClick={() => setWouldChooseAgain(v)} className={`rt-tab ${wouldChooseAgain === v ? 'rt-tab-active' : ''}`} data-testid={`ct-choose-${v}`}>
                  {v === 'yes' ? 'Yes' : v === 'no' ? 'No' : 'Maybe'}
                </button>
              ))}
            </div>
          </div>
          <div className="rt-field">
            <label className="rt-form-label">Display my name as</label>
            <select value={displayMode} onChange={(e) => setDisplayMode(e.target.value)} className="rt-select" data-testid="ct-display-mode">
              <option value="anonymous">Post anonymously</option>
              <option value="first_name">Show only my first name</option>
              <option value="full">Show my full name</option>
            </select>
          </div>
          {displayMode !== 'anonymous' && (<div className="rt-field"><label className="rt-form-label">Your Name</label><input type="text" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} placeholder={displayMode === 'first_name' ? 'First name only is fine' : 'Full name'} className="rt-input" maxLength={80} data-testid="rt-reviewer-name" /></div>)}
          <div className="rt-field">
            <label className="rt-form-label">Mobile Number<span className="rt-optional">(for mystery gift delivery only, never shown publicly)</span></label>
            <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile number" className="rt-input" maxLength={10} pattern="\d{10}" data-testid="rt-mobile-input" />
          </div>
          <div className="rt-field rt-anonymous-toggle">
            <label className="rt-toggle-label">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="rt-checkbox" data-testid="ct-consent" />
              <span className="rt-toggle-text">I confirm this is my genuine personal experience. CollegeUnlock may publish it after verification.</span>
            </label>
          </div>
          {error && <div className="rt-form-error" data-testid="rt-form-error">{error}</div>}
          <button type="submit" disabled={submitting} className="rt-submit-btn" data-testid="rt-submit-btn">
            {submitting ? (<span className="rt-spinner-wrap"><span className="rt-spinner" /> Submitting...</span>) : ('Submit My Campus Truth →')}
          </button>
        </form>
      </div>
    </section>
  );
}
